import React, { useState } from 'react';
import Navbar from '../../components/common/Navbar/Navbar';
import Footer from '../../components/common/Footer/Footer';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import './ContactPage.css';

interface FormData {
  // Passo 1: Área de Interesse
  serviceType: string;

  // Passo 2: Detalhes do Projeto
  assetType: string[];
  estimatedVolume: string;
  timeframe: string;
  projectDescription: string;

  // Passo 3: Informações Pessoais
  fullName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
}

const ContactPage: React.FC = () => {
  const { t, currentLanguage, setLanguage } = usePublicTranslation();

  // Extract FAQ data from translations for SEO schema
  const faqItems = t.faq?.contact?.items || [];

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    serviceType: '',
    assetType: [],
    estimatedVolume: '',
    timeframe: '',
    projectDescription: '',
    fullName: '',
    email: '',
    phone: '',
    company: '',
    position: ''
  });

  // FAQ Schema generator
  const generateFAQSchema = (items: any[]) => {
    if (!items || items.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  };

  // SEO Configuration
  const title = currentLanguage === 'pt-br'
    ? 'Contato - Fale com Especialistas em Mineração'
    : currentLanguage === 'en'
      ? 'Contact - Talk to Mining Specialists'
      : 'Contacto - Hable con Especialistas en Minería';

  const description = currentLanguage === 'pt-br'
    ? 'Entre em contato com a NexuCapital. Especialistas em intermediação de ativos minerários, due diligence e compliance. Atendimento personalizado.'
    : currentLanguage === 'en'
      ? 'Contact NexuCapital. Specialists in mining asset intermediation, due diligence and compliance. Personalized service.'
      : 'Contacte NexuCapital. Especialistas en intermediación de activos mineros, due diligence y cumplimiento. Servicio personalizado.';

  useSEO({
    title,
    description,
    path: '/contact',
    keywords: 'contato NexuCapital, falar mineração, consultoria ativos minerários, contato mineração Brasil',
    language: currentLanguage as 'pt-br' | 'en' | 'es',
    structuredData: [
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: currentLanguage === 'pt-br' ? 'Contato' : currentLanguage === 'en' ? 'Contact' : 'Contacto', url: '/contact' },
      ]),
      generateWebPageSchema({
        name: title,
        description: description,
        url: '/contact',
        datePublished: "2025-01-20",
        dateModified: "2025-01-20"
      }),
      generateFAQSchema(faqItems)
    ].filter(Boolean),
  });

  const services = [
    {
      id: 'markets',
      title: 'NexuCapital Markets',
      description: 'Intermediação e negociação de ativos minerários',
      icon: '💰'
    },
    {
      id: 'advisory',
      title: 'NexuCapital Advisory',
      description: 'Consultoria técnica e regulatória',
      icon: '🎯'
    },
    {
      id: 'origins',
      title: 'NexuCapital Origins',
      description: 'Certificação e rastreamento de origem',
      icon: '🔍'
    },
    {
      id: 'logistics',
      title: 'NexuCapital Logistics',
      description: 'Cadeia física segura e logística',
      icon: '🚛'
    },
    {
      id: 'vault',
      title: 'NexuCapital Vault',
      description: 'Custódia segura e estruturas financeiras',
      icon: '🏦'
    },
    {
      id: 'insights',
      title: 'NexuCapital Insights',
      description: 'Inteligência de mercado e análises',
      icon: '📊'
    }
  ];

  const assetTypes = [
    { id: 'gold', label: 'Ouro', icon: '🟨' },
    { id: 'diamond', label: 'Diamantes', icon: '💎' },
    { id: 'gemstones', label: 'Pedras Preciosas', icon: '💍' },
    { id: 'iron', label: 'Minério de Ferro', icon: '⚫' },
    { id: 'copper', label: 'Cobre', icon: '🟤' },
    { id: 'rare-earth', label: 'Terras Raras', icon: '🧪' },
    { id: 'other', label: 'Outros Minerais', icon: '⚡' }
  ];

  const timeframes = [
    { value: 'immediate', label: 'Imediato (até 30 dias)' },
    { value: 'short', label: 'Curto prazo (1-3 meses)' },
    { value: 'medium', label: 'Médio prazo (3-6 meses)' },
    { value: 'long', label: 'Longo prazo (6+ meses)' },
    { value: 'flexible', label: 'Flexível' }
  ];

  // Validação dos passos
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.serviceType !== '';
      case 2:
        return formData.assetType.length > 0 && formData.timeframe !== '' && formData.projectDescription.trim() !== '';
      case 3:
        return formData.fullName !== '' && formData.email !== '' && formData.phone !== '' && formData.company !== '';
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    // Só permite navegar para trás ou para passos já validados
    if (step <= currentStep || (step === currentStep + 1 && validateStep(currentStep))) {
      setCurrentStep(step);
    }
  };

  const updateFormData = (field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAssetTypeChange = (assetId: string) => {
    const updatedAssetTypes = formData.assetType.includes(assetId)
      ? formData.assetType.filter(id => id !== assetId)
      : [...formData.assetType, assetId];
    updateFormData('assetType', updatedAssetTypes);
  };

  const handleSubmit = () => {
    // Aqui implementaria o envio do formulário
    console.log('Formulário enviado:', formData);
    alert('Formulário enviado com sucesso! Entraremos em contato em breve.');
  };

  return (
    <div className="contact-page">
      <header>
        <Navbar
          currentLanguage={currentLanguage}
          onLanguageChange={setLanguage}
        />
      </header>

      <main className="contact-main">
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="contact-hero-container">
            <h1 className="contact-hero-title">Fale com um Especialista</h1>
            <p className="contact-hero-subtitle">
              Conecte-se com nossos especialistas em mineração e descubra como podemos ajudar seu projeto
            </p>
          </div>
        </section>

        {/* Formulário com Stepper */}
        <section className="contact-form-section">
          <div className="contact-form-container">

            {/* Stepper Indicator */}
            <div className="stepper-container">
              <div className="stepper">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="stepper-item">
                    <div
                      className={`stepper-circle ${step === currentStep ? 'active' :
                          step < currentStep ? 'completed' : 'pending'
                        } ${validateStep(step) || step < currentStep ? 'clickable' : ''}`}
                      onClick={() => handleStepClick(step)}
                    >
                      {step < currentStep ? '✓' : step}
                    </div>
                    <div className="stepper-label">
                      {step === 1 && 'Área de Interesse'}
                      {step === 2 && 'Detalhes do Projeto'}
                      {step === 3 && 'Suas Informações'}
                      {step === 4 && 'Revisão e Envio'}
                    </div>
                    {step < 4 && <div className={`stepper-line ${step < currentStep ? 'completed' : ''}`} />}
                  </div>
                ))}
              </div>
            </div>

            {/* Conteúdo dos Passos */}
            <div className="step-content">

              {/* Passo 1: Área de Interesse */}
              {currentStep === 1 && (
                <div className="step step-1">
                  <h2 className="step-title">Em que podemos ajudá-lo?</h2>
                  <p className="step-description">Selecione a área de especialização que melhor se adequa às suas necessidades</p>

                  <div className="services-grid">
                    {services.map((service) => (
                      <div
                        key={service.id}
                        className={`service-card ${formData.serviceType === service.id ? 'selected' : ''}`}
                        onClick={() => updateFormData('serviceType', service.id)}
                      >
                        <div className="service-icon">{service.icon}</div>
                        <h3 className="service-title">{service.title}</h3>
                        <p className="service-description">{service.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Passo 2: Detalhes do Projeto */}
              {currentStep === 2 && (
                <div className="step step-2">
                  <h2 className="step-title">Detalhes do seu projeto</h2>
                  <p className="step-description">Nos conte mais sobre os ativos e o escopo do projeto</p>

                  <div className="form-section">
                    <h3>Tipo de Ativo Minerário</h3>
                    <div className="asset-types-grid">
                      {assetTypes.map((asset) => (
                        <div
                          key={asset.id}
                          className={`asset-card ${formData.assetType.includes(asset.id) ? 'selected' : ''}`}
                          onClick={() => handleAssetTypeChange(asset.id)}
                        >
                          <span className="asset-icon">{asset.icon}</span>
                          <span className="asset-label">{asset.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="estimatedVolume">Volume Estimado</label>
                      <input
                        type="text"
                        id="estimatedVolume"
                        value={formData.estimatedVolume}
                        onChange={(e) => updateFormData('estimatedVolume', e.target.value)}
                        placeholder="Ex: 100kg, 1000 toneladas, 50 quilates"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="timeframe">Prazo do Projeto</label>
                      <select
                        id="timeframe"
                        value={formData.timeframe}
                        onChange={(e) => updateFormData('timeframe', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Selecione o prazo</option>
                        {timeframes.map((tf) => (
                          <option key={tf.value} value={tf.value}>{tf.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="projectDescription">Descrição do Projeto</label>
                    <textarea
                      id="projectDescription"
                      value={formData.projectDescription}
                      onChange={(e) => updateFormData('projectDescription', e.target.value)}
                      placeholder="Descreva seu projeto, objetivos e quaisquer requisitos específicos..."
                      className="form-textarea"
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {/* Passo 3: Informações Pessoais */}
              {currentStep === 3 && (
                <div className="step step-3">
                  <h2 className="step-title">Suas informações de contato</h2>
                  <p className="step-description">Para que possamos entrar em contato e preparar uma proposta personalizada</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fullName">Nome Completo *</label>
                      <input
                        type="text"
                        id="fullName"
                        value={formData.fullName}
                        onChange={(e) => updateFormData('fullName', e.target.value)}
                        placeholder="Seu nome completo"
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        placeholder="seu.email@exemplo.com"
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Telefone *</label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value)}
                        placeholder="+55 (11) 99999-9999"
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="company">Empresa *</label>
                      <input
                        type="text"
                        id="company"
                        value={formData.company}
                        onChange={(e) => updateFormData('company', e.target.value)}
                        placeholder="Nome da sua empresa"
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="position">Cargo/Posição</label>
                    <input
                      type="text"
                      id="position"
                      value={formData.position}
                      onChange={(e) => updateFormData('position', e.target.value)}
                      placeholder="Seu cargo na empresa"
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              {/* Passo 4: Revisão e Envio */}
              {currentStep === 4 && (
                <div className="step step-4">
                  <h2 className="step-title">Revisão das informações</h2>
                  <p className="step-description">Confirme se todas as informações estão corretas antes do envio</p>

                  <div className="review-section">
                    <div className="review-item">
                      <h3>Serviço Solicitado</h3>
                      <p>{services.find(s => s.id === formData.serviceType)?.title}</p>
                    </div>

                    <div className="review-item">
                      <h3>Tipos de Ativo</h3>
                      <p>{formData.assetType.map(id => assetTypes.find(a => a.id === id)?.label).join(', ')}</p>
                    </div>

                    <div className="review-item">
                      <h3>Volume Estimado</h3>
                      <p>{formData.estimatedVolume || 'Não informado'}</p>
                    </div>

                    <div className="review-item">
                      <h3>Prazo</h3>
                      <p>{timeframes.find(t => t.value === formData.timeframe)?.label}</p>
                    </div>

                    <div className="review-item">
                      <h3>Contato</h3>
                      <p>{formData.fullName} - {formData.company}</p>
                      <p>{formData.email} | {formData.phone}</p>
                    </div>

                    <div className="review-item">
                      <h3>Descrição do Projeto</h3>
                      <p>{formData.projectDescription}</p>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Botões de Navegação */}
            <div className="step-navigation">
              {currentStep > 1 && (
                <button
                  className="btn btn-secondary"
                  onClick={handlePrev}
                >
                  ← Voltar
                </button>
              )}

              <div className="nav-spacer"></div>

              {currentStep < 4 ? (
                <button
                  className={`btn btn-primary ${!validateStep(currentStep) ? 'disabled' : ''}`}
                  onClick={handleNext}
                  disabled={!validateStep(currentStep)}
                >
                  Próximo →
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={handleSubmit}
                >
                  Enviar Solicitação
                </button>
              )}
            </div>

          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;